import React, { useMemo } from 'react';
import { CSSZSickLeaveCertificate } from '../../Interfaces/CSSZ.interface';
import styles from './CSSZSickLeaveCertificateAttendingDoctorReport.module.scss';
import Text from '../../../../Components/Base/Text/Text';
import CSSZSickLeaveCertificateAttendingDoctorReportForm, {
	CSSZSickLeaveCertificateAttendingDoctorReportFormValues,
} from './CSSZSickLeaveCertificateAttendingDoctorReportForm';
import { DeepPartial } from '../../../../Interfaces/DeepPartial';
import useCSSZSickLeaveForm from '../Hooks/useCSSZSickLeaveForm';
import Loading from '../../../../Components/Loading';
import { DocumentSubType } from '../../../../graphql/graphql';
import { checkForSubmission } from '../../Helpers/submissions.helper';
import { SUBMISSION_STATUS_KEYS } from '../../Helpers/sickLeaveStatusMap.helper';

interface Props {
	item: CSSZSickLeaveCertificate;
	defaultValues?: DeepPartial<CSSZSickLeaveCertificateAttendingDoctorReportFormValues>;
	patientId: string;
	onCreate: () => void;
}

export type CSSZSickLeaveCertificateAttendingDoctorReportValues = Omit<
	CSSZSickLeaveCertificateAttendingDoctorReportFormValues,
	'changeDiagnosisData' | 'releaseData' | 'allowAddressChangeData'
> & {
	changeDiagnosisData?: {
		dateFrom: string;
		diagnosis: string;
	};
	releaseData?: {
		dateFrom: string;
		forwardedTo: string;
	};
	allowAddressChangeData?: {
		validFrom: string;
		validTo?: string;
		patientAddress: {
			typeOfAddress?: string;
			state: string;
			street?: string;
			guideNumber?: string;
			descriptiveNumber: string;
			floor?: string;
			postCode: string;
			city: string;
			partOfCity?: string;
			note?: string;
		};
	};
};

const CSSZSickLeaveCertificateAttendingDoctorReport: React.FC<Props> = ({
	item,
	defaultValues,
	patientId,
	onCreate,
}) => {
	const { post, isLoading } = useCSSZSickLeaveForm({
		patientId,
		sickLeaveCertificate: item,
		endpoint: 'hol',
	});

	const individualOutingsLocked = useMemo(
		() =>
			!checkForSubmission(item.submissions, DocumentSubType.ChooseOutings, [
				SUBMISSION_STATUS_KEYS.CSSZ_ACCEPTED,
			]),
		[item],
	);

	const handleSubmit = async (
		values: CSSZSickLeaveCertificateAttendingDoctorReportFormValues,
	) => {
		const payload: CSSZSickLeaveCertificateAttendingDoctorReportValues = {
			...values,
			changeDiagnosisData: values.changeDiagnosisData && {
				...values.changeDiagnosisData,
				diagnosis: values.changeDiagnosisData.diagnosis.value as string,
			},
			releaseData: values.releaseData && {
				...values.releaseData,
				forwardedTo: values.releaseData.forwardedTo.value as string,
			},
			allowAddressChangeData: values.allowAddressChangeData && {
				...values.allowAddressChangeData,
				patientAddress: {
					...values.allowAddressChangeData.patientAddress,
					typeOfAddress: values.allowAddressChangeData.patientAddress
						?.typeOfAddress?.value as string,
					state: values.allowAddressChangeData.patientAddress?.state
						?.value as string,
				},
			},
		};

		await post(payload, onCreate);
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.header}>
				<div className={styles.inline}>
					<Text
						tx='component.CSSZSickLeaveCertificateAttendingDoctorReport.evidenceNumber'
						className={styles.title}
						colon
					/>
					<Text className={styles.bold}>{item.data.decisionNumber}</Text>
				</div>
			</div>
			<div className={styles.items}>
				<div className={styles.inline}>
					<Text
						tx='component.CSSZSickLeaveCertificateAttendingDoctorReport.surnameAndName'
						className={styles.title}
						colon
					/>
					<Text className={styles.bold}>{item.data.userName}</Text>
				</div>
				<div className={styles.inline}>
					<Text
						tx='component.CSSZSickLeaveCertificateAttendingDoctorReport.birthNumber'
						className={styles.title}
						colon
					/>
					<Text className={styles.bold}>{item.data.birthNumber}</Text>
				</div>
			</div>
			<CSSZSickLeaveCertificateAttendingDoctorReportForm
				onSubmit={handleSubmit}
				defaultValues={defaultValues}
				individualOutingsLocked={individualOutingsLocked}
				patientId={patientId}
			/>
		</div>
	);
};

export default CSSZSickLeaveCertificateAttendingDoctorReport;
